import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Smartphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const ios = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const b64 = (v) => {
  const s = (v + "=".repeat((4 - (v.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
};
const device = () => (ios() ? "iPhone/iPad" : /android/i.test(navigator.userAgent) ? "Android" : "Computer");

async function currentSub() {
  if (!supported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

export function PushToggle() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    currentSub().then((s) => setOn(!!s)).catch(() => {});
  }, []);
  const enable = async () => {
    setBusy(true);
    try {
      const cfg = (await api.get("/push/config")).data;
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      if ((await Notification.requestPermission()) !== "granted") throw new Error("Permesso negato: abilita le notifiche nelle impostazioni del telefono");
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64(cfg.publicKey) }));
      await api.post("/push/subscribe", { ...sub.toJSON(), device: device() });
      setOn(true);
      try {
        await api.post("/push/test");
        toast.success("Notifiche attive su questo dispositivo: ti è arrivata una notifica di prova");
      } catch {
        toast.success("Notifiche attive su questo dispositivo");
      }
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    try {
      const sub = await currentSub();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setOn(false);
      toast.success("Notifiche disattivate su questo dispositivo");
    } catch (e) {
      toast.error(e.response ? errMsg(e) : "Non riesco a disattivare: riprova");
    }
  };
  if (ios() && !standalone()) {
    return <div className="px-4 py-3 text-xs text-[#47606B] bg-[#FFF8E6] border-b border-[#F3E2B3]" data-testid="push-ios-help"><Smartphone size={13} className="inline mr-1" />Per ricevere le notifiche su iPhone: tocca <b>Condividi</b> → <b>Aggiungi alla schermata Home</b>, apri l'app dall'icona e torna qui.</div>;
  }
  if (!supported()) return <div className="px-4 py-3 text-xs text-[#7B929C] border-b border-[#EEF4F6]" data-testid="push-unsupported">Questo browser non supporta le notifiche sul telefono.</div>;
  return (
    <div className="px-4 py-3 border-b border-[#EEF4F6] flex items-center justify-between gap-2" data-testid="push-toggle">
      <span className="text-xs text-[#47606B] flex items-center gap-1.5"><BellRing size={13} /> {on ? "Notifiche attive su questo dispositivo" : "Ricevi le notifiche anche ad app chiusa"}</span>
      <button disabled={busy} onClick={on ? disable : enable} className={`text-xs rounded-full px-3 py-1 ${on ? "border border-[#D4E6EC] text-[#47606B]" : "bg-[#0B6E8F] text-white"}`} data-testid={on ? "push-disable" : "push-enable"}>{busy ? "…" : on ? "Disattiva" : "Attiva"}</button>
    </div>
  );
}
